import React, {useState} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components/macro';
import { CardElement } from '@stripe/react-stripe-js';
import { Field } from 'redux-form';
import useResizeObserver from '../../../utils/useResizeObserver';

import InputGroup from '../../InputGroup';
import cardHelper from 'creditcards/card';

import { PaymentMethod } from '../../../utils/enums';


const parseExpDate = value => {
    const digitsOnly = (value || '').replace(/\D/g, '');
    return digitsOnly == null || digitsOnly === '' ? null : digitsOnly.replace(/\D/g, '');
};

const setCarotPosition = ev => {
    setTimeout(() => {
        var target = ev.target;
        var value = target.value;
        target.selectionStart = target.selectionEnd = value.length;
    }, 50);
};

const formatExpDate = (value, unParsedPrevValue) => {
    let newValue = value;

    if (value && value.length > 1) {
        newValue = `${value.slice(0, 2)}`;
        if (value.length === 2 && unParsedPrevValue && unParsedPrevValue.endsWith(' / '))
            return newValue;

        newValue += ' / ' + value.slice(2);
    }

    if (newValue && newValue.length === 9) {
        return newValue.slice(0, 5) + newValue.slice(-2);
    }

    return newValue;
};

const nonCCInputsMinSize = 185, ccFullLength = 165;
const minLengthToFitEverything = ccFullLength + nonCCInputsMinSize

const CreditCardInput = props => {

    if (!props.isOrgHunter){
        if ((props.multiInputWidth < minLengthToFitEverything) &&
            (!(props.focusedInput === 'cc') || !props.outerDivHasFocus) && props.expandCCInput){
            props.setExpandCCInput(false);
        }
        else if (((props.multiInputWidth >= minLengthToFitEverything) ||
                    (props.focusedInput === 'cc' && props.outerDivHasFocus)) && !props.expandCCInput) {
            props.setExpandCCInput(true);
        }
    }

    const label = props.expandCCInput || props.isOrgHunter ? 'Card Number  ' : 'Card';

    return (<Field
        multiInputProps={props.multiInputProps}
        containerStyle={{
            flexBasis: props.expandCCInput && `${ccFullLength}px`,
            flexGrow: props.expandCCInput ? 0 : 1,
            transition: 'all .2s linear'
        }}
        inputStyle={{
            borderRight: 'none',
            borderRadius: '2px 0 0 2px',
            paddingRight: 0
        }}
        labelStyle={{
            whiteSpace: 'pre-wrap'
        }}
        name="cardNumber"
        isShortRow={props.isShortRow}
        className={'fs-block'}
        onFocus={()=> props.setFocusedInput?.('cc')}
        onBlur={() => props.checkForBlurError('cardNumber')}
        submissionError={props.errors.includes('cardNumber')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        customInput={props.customInput}
        normalize={props.customInput ? null : value => cardHelper.format(value)}
        parse={props.customInput ? null : value => cardHelper.parse(value)}
        inputmode="numeric"
        label={label}
        customErrorLabel={!props.isOrgHunter && label}
        autocomplete="cc-number"
        onChange={props.customInput ? null : ev => setCarotPosition(ev)}
        onValidityChange={props.onValidityChange}
    />);
}

CreditCardInput.propTypes = {
    customInput: PropTypes.func,
    onValidityChange: PropTypes.func,
    isStripeDataValid: PropTypes.bool,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const ExpirationDateInput = props => (
    <Field
        multiInputProps={props.multiInputProps}
        containerStyle={{
            borderRadius: 0
        }}
        inputStyle={{
            borderLeft: 'none',
            borderRight: 'none',
            borderRadius: 0
        }}
        name="expDate"
        className={'fs-block'}
        onFocus={()=> props.setFocusedInput('details')}
        onBlur={() => props.checkForBlurError('expDate')}
        submissionError={props.errors.includes('expDate')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        customInput={props.customInput}
        label="MM/YY"
        normalize={
            props.customInput
                ? null
                : (value, unParsedPrevValue) => formatExpDate(value, unParsedPrevValue)
        }
        parse={props.customInput ? null : value => parseExpDate(value)}
        onChange={ev => setCarotPosition(ev)}
        maxLength={7}
        placeholder="MM/YY"
        autocomplete="cc-exp"
        onValidityChange={props.onValidityChange}
        isShortRow={props.isShortRow}
    />
);

ExpirationDateInput.propTypes = {
    customInput: PropTypes.func,
    onValidityChange: PropTypes.func,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const CvvInput = props => (
    <Field
        multiInputProps={props.multiInputProps}
        containerStyle={{
            borderRadius: 0,
            flexGrow: .7
        }}
        inputStyle={{
            borderLeft: 'none',
            borderRight: props.hideZipCode ? null : 'none',
            borderRadius: props.hideZipCode ? '0 2px 2px 0' : 0
        }}
        name="cvv"
        className={'fs-block'}
        onFocus={()=> props.setFocusedInput('details')}
        onBlur={() => props.checkForBlurError('cvv')}
        submissionError={props.errors.includes('cvv')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        customInput={props.customInput}
        type="tel"
        inputmode="numeric"
        label="CVV"
        autocomplete="cc-csc"
        placeholder="CVV"
        maxLength={4}
        onValidityChange={props.onValidityChange}
        isShortRow={props.isShortRow}
    />
);

CvvInput.propTypes = {
    customInput: PropTypes.func,
    onValidityChange: PropTypes.func,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const PostalCodeInput = props => (
    <Field
        multiInputProps={props.multiInputProps}
        containerStyle={{
            transition: 'flex-grow .2s linear',
            flexGrow: props.hideZipCode && 0,
        }}
        inputStyle={{
            borderRadius: '0 2px 2px 0',
            borderLeft: 'none'
        }}
        name="postalCode"
        className={'fs-block'}
        onFocus={()=> props.setFocusedInput('details')}
        onBlur={() => props.checkForBlurError('postalCode')}
        submissionError={props.errors.includes('postalCode')}
        clearSubmitErrors={props.clearSubmitErrors}
        component={InputGroup}
        type="text"
        label={'ZIP'}
        placeholder={'ZIP'}
        autocomplete="billing postal-code"
        maxLength={10}
        isShortRow={props.isShortRow}
    />
);

PostalCodeInput.propTypes = {
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

class OrgHunterCreditCardInputs extends React.Component {
    constructor(props){
        super(props);
        this.props.setIsStripeDataValid(false);
    }

    setValidity(isValid){
        this.props.onOrgHunterCcValidityChange('cardNumber', isValid);
        this.props.onOrgHunterCcValidityChange('expDate', isValid);
        this.props.onOrgHunterCcValidityChange('cvv', isValid);
    }

    render() {
        return (
            <CreditCardInput
                isOrgHunter
                isShortRow={this.props.isShortRow}
                checkForBlurError={this.props.checkForBlurError}
                errors={this.props.errors}
                clearSubmitErrors={this.props.clearSubmitErrors}
                customInput={CardElement}
                onValidityChange={isValid => {
                    this.setValidity(isValid);
                    this.setState({isValid});
                    this.props.setIsStripeDataValid(isValid);
                }}
                isStripeDataValid={this.props.isStripeDataValid}
            />
        );
    }
}

OrgHunterCreditCardInputs.propTypes = {
    onOrgHunterCcValidityChange: PropTypes.func,
    errors: PropTypes.array,
    clearSubmitErrors: PropTypes.func,
    checkForBlurError: PropTypes.func
};

const CreditCardInputContainer = styled.div`
    display: flex;
`

const RegularCreditCardInputs = props => {
    const [isFocused, setIsFocused] = useState(false);
    const [isValid, setIsValid] = useState(null);
    const multiInputProps = {isFocused, setIsFocused, isValid, setIsValid};
    const { ref, width } = useResizeObserver();

    const [expandCCInput, setExpandCCInput] = useState(false);
    const [focusedInput, setFocusedInput] = useState(null);
    const [outerDivHasFocus, setOuterDivHasFocus] = useState(false);

    const hideZipCode = width < minLengthToFitEverything
                            && focusedInput !== 'details' && outerDivHasFocus;

    return (<CreditCardInputContainer
        ref={ref}
        onBlur={()=>{setOuterDivHasFocus(false)}}
        onFocus={()=>{setOuterDivHasFocus(true)}}
    >
        <CreditCardInput
            outerDivHasFocus={outerDivHasFocus}
            focusedInput={focusedInput}
            setFocusedInput={setFocusedInput}
            multiInputWidth={width}
            expandCCInput={expandCCInput}
            setExpandCCInput={setExpandCCInput}
            multiInputProps={multiInputProps}
            isShortRow={props.isShortRow}
            checkForBlurError={props.checkForBlurError}
            errors={props.errors}
            clearSubmitErrors={props.clearSubmitErrors}
        />
        <ExpirationDateInput
            setFocusedInput={setFocusedInput}
            multiInputProps={multiInputProps}
            isShortRow={props.isShortRow}
            checkForBlurError={props.checkForBlurError}
            errors={props.errors}
            clearSubmitErrors={props.clearSubmitErrors}
        />
        <CvvInput
            setFocusedInput={setFocusedInput}
            hideZipCode={hideZipCode}
            multiInputProps={multiInputProps}
            isShortRow={props.isShortRow}
            checkForBlurError={props.checkForBlurError}
            errors={props.errors}
            clearSubmitErrors={props.clearSubmitErrors} />
        <PostalCodeInput
            setFocusedInput={setFocusedInput}
            hideZipCode={hideZipCode}
            multiInputProps={multiInputProps}
            isShortRow={props.isShortRow}
            checkForBlurError={props.checkForBlurError}
            errors={props.errors}
            clearSubmitErrors={props.clearSubmitErrors}
        />
    </CreditCardInputContainer>)
}

RegularCreditCardInputs.propTypes = {
    errors: PropTypes.array.isRequired,
    clearSubmitErrors: PropTypes.func.isRequired,
    checkForBlurError: PropTypes.func
};

const CreditCardInputs = props => {
    return props.paymentMethod === PaymentMethod.OrgHunter ? (
        <OrgHunterCreditCardInputs {...props} />
    ) : (
        <RegularCreditCardInputs {...props} />
    );
};

CreditCardInputs.propTypes = {
    paymentMethod: PropTypes.string.isRequired,
    onValidityChange: PropTypes.func
};

export default CreditCardInputs;
